import { DynamicLink } from '@/reactRouterPlugins';
import { TaxonKeyQuery } from '@/gql/graphql';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/shadcn';

const LIMIT = 10;
const Synonyms = ({ taxonInfo }: { taxonInfo: NonNullable<TaxonKeyQuery['taxonInfo']> }) => {
  const [showAll, setShowAll] = useState(false);

  const count =
    taxonInfo.synonyms?.heterotypic?.flat().concat(taxonInfo.synonyms?.homotypic || []).length ?? 0;
  if (!taxonInfo) return null;
  const datasetKey = taxonInfo.datasetKey;
  const homotypic = taxonInfo.synonyms?.homotypic || [];
  const heterotypic = taxonInfo.synonyms?.heterotypic || [];
  return (
    <div>
      <div className="g-text-sm g-text-slate-500 g-mb-1">
        <FormattedMessage id="counts.nResults" values={{ total: count }} />
        {count > LIMIT && (
          <Button variant="link" onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? 'Show Less' : `Show All`}
          </Button>
        )}
      </div>
      <ul>
        {homotypic.slice(0, showAll ? undefined : LIMIT).map((synonym) => {
          return (
            <li key={synonym.taxonID} className="g-py-1 g-border-t g-border-gray-200">
              <Synonym synonym={synonym} type="homotypic" datasetKey={datasetKey} />
            </li>
          );
        })}
        {heterotypic
          ?.filter((synonyms): synonyms is NonNullable<typeof synonyms> => synonyms != null)
          .slice(0, showAll ? undefined : Math.max(0, LIMIT - (homotypic?.length || 0)))
          .map((synonyms) => {
            const first = synonyms[0];
            const remaining = synonyms.slice(1);
            return (
              <li key={first.taxonID} className="g-py-1 g-border-t g-border-gray-200">
                <Synonym synonym={first} type="heterotypic" datasetKey={datasetKey} />
                {remaining.map((synonym) => (
                  <ul key={synonym.taxonID} className="g-ms-4">
                    <li>
                      <Synonym synonym={synonym} type="homotypic" datasetKey={datasetKey} />
                    </li>
                  </ul>
                ))}
              </li>
            );
          })}
      </ul>
    </div>
  );
};

function Synonym({
  synonym,
  type,
  datasetKey,
}: {
  synonym: {
    taxonID: string;
    label: string;
    isOriginalNameUsage?: boolean;
  };
  type: 'homotypic' | 'heterotypic';
  datasetKey: string;
}) {
  return (
    <>
      <DynamicLink
        pageId="taxonKey"
        variables={{ key: synonym.taxonID, datasetKey }}
        className="g-text-decoration-none g-text-primary-500"
      >
        {type === 'homotypic' ? '≡ ' : '= '}
        <span
          className=" hover:g-underline"
          dangerouslySetInnerHTML={{ __html: synonym.label }}
        ></span>
      </DynamicLink>
      {synonym.isOriginalNameUsage && (
        <InfoPill className="g-ms-2">
          <FormattedMessage id="taxon.originalNameUsage" />
        </InfoPill>
      )}
    </>
  );
}

export function InfoPill({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        'g-px-1.5 g-py-px g-bg-blue-100 g-text-blue-700 g-border g-border-blue-300 g-rounded g-text-xs g-font-medium g-whitespace-nowrap',
        className
      )}
    >
      {children}
    </span>
  );
}

export default Synonyms;
